/**
 * Offline write queue (T52 — `_docs/01-core/pwa-contract.md` §7).
 *
 * The one IO-bearing module under `app/lib/`. It lives here rather than in `app/store/`
 * because it must be importable from **both** the window and the service worker, and
 * `app/store/` is window-only Zustand. IndexedDB is available in both scopes.
 *
 * Backed by IndexedDB, not memory, on purpose: an in-memory queue dies with the tab, and
 * the tab dying is exactly the offline case — she opens the app, loses signal, the phone
 * sleeps, the tab is evicted.
 *
 * PRIVACY NOTE, read before extending. Queueing a journal save necessarily writes her
 * text to the device, which is the thing `cache-policy.ts` exists to prevent for reads.
 * That is a deliberate, narrow exception: the alternative is losing work she chose to
 * write. It is bounded by deleting each entry the moment its replay is acknowledged, and
 * by never queueing anything not explicitly enqueued by a caller. Do not widen it. The one
 * write held after an answer (`X-Replay: keep`, T942) is held at most `MAX_REPLAY_AGE_MS`.
 */

import { MAX_REPLAY_AGE_MS } from "~/lib/domain/offline-replay";

const DB_NAME = "tfc-offline";
const DB_VERSION = 1;
const STORE = "writes";

/** Background Sync registration tag; the worker listens for this exact string. */
export const SYNC_TAG = "tfc-offline-writes";

interface QueuedWrite {
  /** Assigned by IndexedDB. */
  readonly id?: number;
  readonly url: string;
  readonly method: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: string | null;
  readonly queuedAt: number;
  /**
   * Sent as `X-Idempotency-Key` so a replay can be recognised as a duplicate.
   *
   * The gap this comment used to name is closed (T516): `completeRitualSession` stores
   * the key on `session_progress` behind a unique index and returns early when it sees
   * one it already has. That is what makes the queue safe for a write that increments a
   * counter — T353's duplicate guard could not do it, because its window is 90 seconds
   * and the case here is a phone that reconnects the next morning.
   *
   * The pulse endpoint consumes no key and does not need one: its own unique index on
   * (user_id, session_id) already makes a second arrival a no-op.
   */
  readonly idempotencyKey: string;
}

export interface DrainResult {
  readonly sent: number;
  readonly kept: number;
  readonly dropped: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function tx<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = run(db.transaction(STORE, mode).objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function enqueue(
  write: Omit<QueuedWrite, "id" | "queuedAt" | "idempotencyKey"> &
    Partial<Pick<QueuedWrite, "queuedAt" | "idempotencyKey">>,
): Promise<void> {
  const db = await openDb();
  try {
    await tx(db, "readwrite", (store) =>
      store.add({
        ...write,
        queuedAt: write.queuedAt ?? Date.now(),
        idempotencyKey: write.idempotencyKey ?? crypto.randomUUID(),
      }),
    );
  } finally {
    db.close();
  }
}

async function listQueued(): Promise<QueuedWrite[]> {
  const db = await openDb();
  try {
    return await tx<QueuedWrite[]>(db, "readonly", (store) => store.getAll());
  } finally {
    db.close();
  }
}

export async function size(): Promise<number> {
  const db = await openDb();
  try {
    return await tx<number>(db, "readonly", (store) => store.count());
  } finally {
    db.close();
  }
}

async function remove(id: number): Promise<void> {
  const db = await openDb();
  try {
    await tx(db, "readwrite", (store) => store.delete(id));
  } finally {
    db.close();
  }
}

/** What a replay attempt means for the queued entry. */
export type ReplayOutcome = "sent" | "dropped" | "kept";

/**
 * Pure, and extracted on purpose: this is the only interesting decision `drain` makes,
 * and testing it through IndexedDB would cost a test-only dependency for no extra
 * confidence.
 *
 * @param status HTTP status, or `null` when the request never reached the server.
 * @param replayHint the response's `X-Replay` header. `keep` (T942): the write belongs to
 *   another account than the one signed in now, or nobody is signed in; it waits for its
 *   owner instead of dropping — for at most `MAX_REPLAY_AGE_MS` (her decision, 2026-10-24),
 *   the same window past which the server no longer trusts the queued date.
 * @param ageMs how long ago the write was queued.
 */
export function classifyReplay(status: number | null, replayHint: string | null = null, ageMs = 0): ReplayOutcome {
  if (status === null) return "kept";
  if (replayHint === "keep") return ageMs > MAX_REPLAY_AGE_MS ? "dropped" : "kept";
  if (status >= 200 && status < 300) return "sent";
  // A rejected write will be rejected again forever; retrying it on every reconnect is a
  // loop, not resilience. 5xx is the opposite — the server is having a bad minute.
  if (status >= 400 && status < 500) return "dropped";
  return "kept";
}

let draining = false;

/**
 * Replays every queued write once, oldest first.
 *
 * Single-flight: concurrent triggers (an `online` event and a Background Sync firing
 * together) must not replay the same entry twice. A second call while a drain is in
 * progress returns zeroes rather than queueing behind it.
 *
 * Outcome per entry:
 * - 2xx  → removed (`sent`).
 * - 4xx  → removed (`dropped`). A rejected write will be rejected again forever;
 *          retrying it on every reconnect is a loop, not resilience.
 *          Except one the server marks `X-Replay: keep` (T942): another account's write,
 *          or any write while nobody is signed in, kept until its owner is signed in on
 *          this device again — at most `MAX_REPLAY_AGE_MS` after it was queued.
 * - 5xx / network failure → kept for the next drain.
 */
export async function drain(fetchImpl: typeof fetch = fetch): Promise<DrainResult> {
  if (draining) return { sent: 0, kept: 0, dropped: 0 };
  draining = true;
  let sent = 0;
  let kept = 0;
  let dropped = 0;
  try {
    const queued = (await listQueued()).sort((a, b) => a.queuedAt - b.queuedAt);
    for (const write of queued) {
      if (write.id === undefined) continue;
      let status: number | null = null;
      let replayHint: string | null = null;
      try {
        const response = await fetchImpl(write.url, {
          method: write.method,
          headers: {
            ...write.headers,
            "X-Idempotency-Key": write.idempotencyKey,
            // T516 — WHEN she did it, not when the phone got signal back. The server
            // stamps its own clock otherwise, which records a Sunday session as
            // Monday's and can cost her the week. Bounded server-side
            // (`resolveCompletedAt`): a client clock is not trusted, only heard.
            "X-Queued-At": String(write.queuedAt),
          },
          body: write.body,
          credentials: "same-origin",
        });
        status = response.status;
        replayHint = response.headers.get("X-Replay");
      } catch {
        status = null;
      }
      const outcome = classifyReplay(status, replayHint, Date.now() - write.queuedAt);
      if (outcome === "sent") {
        await remove(write.id);
        sent += 1;
      } else if (outcome === "dropped") {
        await remove(write.id);
        dropped += 1;
      } else {
        kept += 1;
      }
    }
  } finally {
    draining = false;
  }
  return { sent, kept, dropped };
}
